import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const TRIMESTRES = [
  { key: "t1", label: "T1" },
  { key: "t2", label: "T2" },
  { key: "t3", label: "T3" },
  { key: "t4", label: "T4" },
];

const ESTADO_CFG = {
  pendiente: { label: "En revisión", bg: "#fef3c7", color: "#92400e" },
  aprobado:  { label: "Aprobado",    bg: "#dcfce7", color: "#166534" },
  rechazado: { label: "Devuelto",    bg: "#fee2e2", color: "#991b1b" },
};

function obtenerTrimestreDesdeFecha(fecha) {
  if (!fecha) return "t1";
  const mes = new Date(fecha + "T12:00:00").getMonth() + 1;
  if (mes <= 3) return "t1";
  if (mes <= 6) return "t2";
  if (mes <= 9) return "t3";
  return "t4";
}

export default function SeguimientoIndicador() {
  const { id } = useParams();

  const [indicador, setIndicador]     = useState(null);
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  // -1 = ninguno abierto. Usar número para evitar comparaciones ambiguas
  const [corrigiendo, setCorrigiendo] = useState(-1);
  const [formCorreccion, setFormCorreccion] = useState({});
  const [guardandoCorreccion, setGuardandoCorreccion] = useState(false);
  const [expandedTrimestres, setExpandedTrimestres] = useState(
    { t1: false, t2: false, t3: false, t4: false }
  );
  const [form, setForm] = useState({
    fecha: "", cantidad: "", actividad: "",
    trimestre: "t1", ubicacion_evidencia: "", tipo_evidencia: "",
  });

  const cargarDetalle = async () => {
    setLoading(true);
    try {
      // 1) indicador con cupo por trimestre (pdd_indicador_detalle.php)
      // 2) actividades con estado (pdd_indicador_revision.php)
      const [r1, r2] = await Promise.all([
        fetch(`https://desarrollandoterritorios.com/api/pdd_indicador_detalle.php?id=${id}`, { credentials: "include" }),
        fetch(`https://desarrollandoterritorios.com/api/pdd_indicador_revision.php?id=${id}`, { credentials: "include" }),
      ]);
      const [j1, j2] = await Promise.all([r1.json(), r2.json()]);
      setIndicador(j1.indicador || null);
      setActividades(j2.actividades || []);
    } catch (err) {
      console.error(err);
      setIndicador(null); setActividades([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { cargarDetalle(); }, [id]);

  const actividadesPorTrimestre = TRIMESTRES.reduce((acc, t) => {
    acc[t.key] = actividades.filter(
      (item) => obtenerTrimestreDesdeFecha(item.fecha) === t.key
    );
    return acc;
  }, {});

  const anio = new Date().getFullYear();

  // Cupo viene de pdd_indicador_detalle.php — campos 2026_t1, 2026_t2, etc.
  const getCupo = (qKey) => Number(indicador?.[`${anio}_${qKey}`] ?? 0);

  // Ejecutado: solo actividades NO rechazadas para no contar las devueltas
  const getEjecutado = (qKey) =>
    (actividadesPorTrimestre[qKey] || [])
      .filter((a) => a.estado !== "rechazado")
      .reduce((s, a) => s + Number(a.cantidad_realizada || 0), 0);

  const getDisponible = (qKey) => getCupo(qKey) - getEjecutado(qKey);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "fecha") next.trimestre = obtenerTrimestreDesdeFecha(value);
      return next;
    });
  };

  const guardarActividad = async () => {
    if (!form.fecha || !form.actividad || form.cantidad === "" || !form.tipo_evidencia) {
      alert("Completa todos los campos obligatorios");
      return;
    }
    // Guard: solo la secretaría del indicador puede agregar actividades
    const _cu = JSON.parse(localStorage.getItem("auth_user") || "{}");
    const _du = String(_cu?.dependencia ?? "").toUpperCase().trim();
    const _di = String(indicador?.dependencia ?? "").toUpperCase().trim();
    if (_du && _di && _du !== _di) {
      alert(`Solo usuarios de ${_di} pueden registrar actividades en este indicador.`);
      return;
    }
    const cantidad = Number(form.cantidad);
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      alert("La cantidad debe ser mayor a 0");
      return;
    }
    const cupo = getCupo(form.trimestre);
    const disponible = getDisponible(form.trimestre);
    if (cupo > 0 && cantidad > disponible) {
      alert(`Cupo: ${cupo} | Ejecutado: ${getEjecutado(form.trimestre)} | Disponible: ${disponible}`);
      return;
    }
    setSaving(true);
    try {
      const resp = await fetch(
        "https://desarrollandoterritorios.com/api/pdd_guardar_actividad_indicador.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            pdd_id: Number(id),
            fecha: form.fecha,
            cantidad_realizada: cantidad,
            actividad: form.actividad,
            ubicacion_evidencia: form.ubicacion_evidencia,
            tipo_evidencia: form.tipo_evidencia,
          }),
        }
      );
      const json = await resp.json();
      if (!resp.ok || !json.ok) throw new Error(json?.message || `Error HTTP ${resp.status}`);
      setForm({ fecha:"", cantidad:"", actividad:"", trimestre:"t1", ubicacion_evidencia:"", tipo_evidencia:"" });
      await cargarDetalle();
    } catch (err) {
      alert(err.message || "Ocurrió un error al guardar");
    } finally { setSaving(false); }
  };

  const abrirCorreccion = (item) => {
    const itemId = Number(item.id);
    // Si ya está abierto ese, lo cierra. Si no, abre solo ese.
    setCorrigiendo((prev) => (prev === itemId ? -1 : itemId));
    setFormCorreccion({
      fecha:               item.fecha,
      cantidad:            item.cantidad_realizada,
      actividad:           item.actividad,
      ubicacion_evidencia: item.ubicacion_evidencia || "",
      tipo_evidencia:      item.tipo_evidencia || "",
    });
  };

  const guardarCorreccion = async (itemId) => {
    const fc = formCorreccion;
    if (!fc.fecha || !fc.actividad || fc.cantidad === "" || !fc.tipo_evidencia) {
      alert("Completa todos los campos obligatorios");
      return;
    }
    setGuardandoCorreccion(true);
    try {
      const resp = await fetch(
        "https://desarrollandoterritorios.com/api/pdd_corregir_actividad.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            id: Number(itemId),
            fecha: fc.fecha,
            actividad: fc.actividad,
            cantidad_realizada: Number(fc.cantidad),
            ubicacion_evidencia: fc.ubicacion_evidencia,
            tipo_evidencia: fc.tipo_evidencia,
          }),
        }
      );
      const json = await resp.json();
      if (!resp.ok || !json.ok) throw new Error(json?.message || `Error HTTP ${resp.status}`);
      setCorrigiendo(-1);
      await cargarDetalle();
    } catch (err) {
      alert(err.message || "Error al guardar la corrección");
    } finally { setGuardandoCorreccion(false); }
  };

  if (loading) return <div style={{ padding:24 }}>Cargando detalle del indicador...</div>;
  if (!indicador) return <div style={{ padding:24 }}>No se encontró el indicador</div>;

  return (
    <div style={S.page}>
      <div style={S.wrapper}>
        <button type="button" onClick={() => window.location.href = "/seguimiento-pdd"} style={S.backBtn}>
          ← Regresar a indicadores
        </button>

        <div style={{ marginBottom:20 }}>
          <h1 style={{ margin:0, fontSize:34, fontWeight:800, color:"#1f3b64" }}>Detalle del Indicador</h1>
          <div style={{ marginTop:12, display:"grid", gap:8, color:"#334155" }}>
            <div><strong>Código:</strong> {indicador.numero_indicador}</div>
            <div><strong>Nombre:</strong> {indicador.nombre_completo}</div>
            <div><strong>Unidad:</strong> {indicador.unidad_medida}</div>
            <div><strong>Meta cuatrienio:</strong> {indicador.meta_cuatrenio}</div>
          </div>
        </div>

        {/* Formulario nueva actividad */}
        <div style={S.card}>
          <h2 style={{ marginTop:0, color:"#1f3b64" }}>Registrar actividad</h2>
          <div style={S.formGrid}>
            <div>
              <label style={S.label}>Fecha *</label>
              <input type="date" value={form.fecha}
                onChange={(e) => handleChange("fecha", e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={S.label}>Cantidad *</label>
              <input type="number" min="0" step="0.01" value={form.cantidad}
                onChange={(e) => handleChange("cantidad", e.target.value)}
                style={S.input} placeholder="0.00"/>
            </div>
            <div>
              <label style={S.label}>Trimestre</label>
              <input type="text" value={form.trimestre.toUpperCase()} readOnly
                style={{ ...S.input, background:"#f1f5f9" }}/>
            </div>
            <div style={{ gridColumn:"1 / -1" }}>
              <label style={S.label}>Observación / actividad *</label>
              <textarea value={form.actividad}
                onChange={(e) => handleChange("actividad", e.target.value)}
                style={S.textarea} placeholder="Describe la actividad realizada"/>
            </div>
            <div>
              <label style={S.label}>Ubicación evidencia</label>
              <input type="text" value={form.ubicacion_evidencia}
                onChange={(e) => handleChange("ubicacion_evidencia", e.target.value)}
                style={S.input} placeholder="Ej: carpeta, enlace, dependencia..."/>
            </div>
            <div>
              <label style={S.label}>Tipo de evidencia *</label>
              <select value={form.tipo_evidencia}
                onChange={(e) => handleChange("tipo_evidencia", e.target.value)} style={S.input}>
                <option value="">Seleccionar</option>
                <option>Fotografías</option><option>Videos</option>
                <option>Evidencias Documentales</option><option>Cuantitativas</option>
                <option>Acta</option><option>Informe</option><option>Resolución</option>
                <option>Contrato</option><option>Certificado</option><option>Otro</option>
              </select>
            </div>
          </div>
          <button type="button" onClick={guardarActividad} disabled={saving}
            style={{ ...S.saveBtn, ...(saving ? S.saveBtnDisabled : {}) }}>
            {saving ? "Guardando..." : "+ Agregar actividad"}
          </button>
        </div>

        {/* Trimestres */}
        <div style={S.trimGrid}>
          {TRIMESTRES.map((t) => {
            const items      = actividadesPorTrimestre[t.key] || [];
            const cupo       = getCupo(t.key);
            const ejecutado  = getEjecutado(t.key);
            const disponible = getDisponible(t.key);
            const isExpanded = expandedTrimestres[t.key];
            const dispColor  = disponible < 0 ? "#dc2626" : disponible === 0 && cupo > 0 ? "#16a34a" : "#1d4ed8";
            const devueltos  = items.filter(i => i.estado === "rechazado").length;

            return (
              <div key={t.key} style={S.card}>
                <div style={{ display:"flex", justifyContent:"space-between",
                  alignItems:"center", marginBottom:12, flexWrap:"wrap", gap:8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <h3 style={{ margin:0, color:"#1f3b64", fontSize:"1.1rem" }}>{t.label}</h3>
                    {devueltos > 0 && (
                      <span style={{ fontSize:11, background:"#fee2e2", color:"#991b1b",
                        borderRadius:999, padding:"3px 8px", fontWeight:700 }}>
                        {devueltos} devuelt{devueltos > 1 ? "os" : "o"}
                      </span>
                    )}
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <span style={S.badge("#dbeafe","#1d4ed8")}>Cupo: {cupo}</span>
                    <span style={S.badge("#dcfce7","#15803d")}>Ejecutado: {ejecutado}</span>
                    <span style={S.badge(disponible < 0 ? "#fee2e2" : "#f0f9ff", dispColor)}>
                      Disponible: {disponible}
                    </span>
                  </div>
                </div>

                {cupo > 0 && (
                  <div style={{ height:6, background:"#e5e7eb", borderRadius:"999px",
                    marginBottom:12, overflow:"hidden" }}>
                    <div style={{ height:6, borderRadius:"999px",
                      background: ejecutado >= cupo ? "#16a34a" : "#1677c8",
                      width:`${Math.min((ejecutado/cupo)*100,100)}%`, transition:"width 0.3s" }}/>
                  </div>
                )}

                <button type="button"
                  onClick={() => setExpandedTrimestres(p => ({ ...p, [t.key]: !p[t.key] }))}
                  style={S.toggleBtn}>
                  {isExpanded ? "Ocultar actividades" : `Ver actividades (${items.length})`}
                </button>

                {isExpanded && (
                  <div style={{ marginTop:12 }}>
                    {items.length === 0 ? (
                      <div style={{ color:"#64748b", padding:"10px 0", fontSize:"0.9rem" }}>
                        Sin actividades registradas en este trimestre.
                      </div>
                    ) : (
                      <div style={{ display:"grid", gap:10 }}>
                        {items.map((item) => {
                          const numId   = Number(item.id);
                          const est     = ESTADO_CFG[item.estado] || ESTADO_CFG.pendiente;
                          const editando = corrigiendo === numId;

                          return (
                            <div key={item.id} style={{
                              ...S.actCard,
                              borderLeft: item.estado === "rechazado" ? "4px solid #ef4444"
                                : item.estado === "aprobado" ? "4px solid #16a34a"
                                : "4px solid #f59e0b",
                            }}>
                              <div style={{ marginBottom:8 }}>
                                <span style={{ fontSize:12, background:est.bg, color:est.color,
                                  borderRadius:999, padding:"3px 10px", fontWeight:700 }}>
                                  {est.label}
                                </span>
                              </div>

                              {!editando && (
                                <>
                                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr",
                                    gap:"4px 16px", marginBottom:6 }}>
                                    <div><strong>Fecha:</strong> {item.fecha}</div>
                                    <div><strong>Cantidad:</strong> {item.cantidad_realizada}</div>
                                  </div>
                                  <div style={{ marginBottom:4 }}>
                                    <strong>Observación:</strong> {item.actividad}
                                  </div>
                                  {item.ubicacion_evidencia && (
                                    <div style={{ marginBottom:4 }}>
                                      <strong>Ubicación:</strong>{" "}
                                      <a href={item.ubicacion_evidencia} target="_blank" rel="noreferrer"
                                        style={{ color:"#1677c8" }}>{item.ubicacion_evidencia}</a>
                                    </div>
                                  )}
                                  {item.tipo_evidencia && (
                                    <div style={{ marginBottom:8 }}>
                                      <strong>Tipo:</strong>{" "}
                                      <span style={{ background:"#eff6ff", color:"#1d4ed8",
                                        borderRadius:"6px", padding:"2px 8px", fontSize:"0.8rem", fontWeight:600 }}>
                                        {item.tipo_evidencia}
                                      </span>
                                    </div>
                                  )}
                                  {item.estado === "rechazado" && item.observacion_rechazo && (
                                    <div style={{ background:"#fee2e2", color:"#991b1b",
                                      borderRadius:8, padding:"8px 12px", fontSize:13, marginBottom:10 }}>
                                      <strong>Motivo de devolución:</strong> {item.observacion_rechazo}
                                    </div>
                                  )}
                                  {item.estado === "rechazado" && (
                                    <button type="button" onClick={() => abrirCorreccion(item)} style={S.btnCorregir}>
                                      Corregir y reenviar
                                    </button>
                                  )}
                                </>
                              )}

                              {editando && (
                                <div style={{ display:"grid", gap:10 }}>
                                  <div style={S.formGrid}>
                                    <div>
                                      <label style={S.label}>Fecha *</label>
                                      <input type="date" style={S.input} value={formCorreccion.fecha}
                                        onChange={(e) => setFormCorreccion(p => ({ ...p, fecha: e.target.value }))}/>
                                    </div>
                                    <div>
                                      <label style={S.label}>Cantidad *</label>
                                      <input type="number" style={S.input} value={formCorreccion.cantidad}
                                        onChange={(e) => setFormCorreccion(p => ({ ...p, cantidad: e.target.value }))}/>
                                    </div>
                                    <div>
                                      <label style={S.label}>Tipo de evidencia *</label>
                                      <select style={S.input} value={formCorreccion.tipo_evidencia}
                                        onChange={(e) => setFormCorreccion(p => ({ ...p, tipo_evidencia: e.target.value }))}>
                                        <option value="">Seleccionar</option>
                                        <option>Fotografías</option><option>Videos</option>
                                        <option>Evidencias Documentales</option><option>Cuantitativas</option>
                                        <option>Acta</option><option>Informe</option><option>Resolución</option>
                                        <option>Contrato</option><option>Certificado</option><option>Otro</option>
                                      </select>
                                    </div>
                                    <div style={{ gridColumn:"1 / -1" }}>
                                      <label style={S.label}>Observación / actividad *</label>
                                      <textarea style={S.textarea} value={formCorreccion.actividad}
                                        onChange={(e) => setFormCorreccion(p => ({ ...p, actividad: e.target.value }))}/>
                                    </div>
                                    <div style={{ gridColumn:"1 / -1" }}>
                                      <label style={S.label}>Ubicación evidencia</label>
                                      <input type="text" style={S.input} value={formCorreccion.ubicacion_evidencia}
                                        onChange={(e) => setFormCorreccion(p => ({ ...p, ubicacion_evidencia: e.target.value }))}/>
                                    </div>
                                  </div>
                                  <div style={{ display:"flex", gap:10 }}>
                                    <button type="button" disabled={guardandoCorreccion}
                                      onClick={() => guardarCorreccion(numId)}
                                      style={{ ...S.saveBtn, ...(guardandoCorreccion ? S.saveBtnDisabled : {}) }}>
                                      {guardandoCorreccion ? "Enviando..." : "Enviar corrección"}
                                    </button>
                                    <button type="button" onClick={() => setCorrigiendo(-1)} style={S.btnCancelar}>
                                      Cancelar
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const S = {
  page:    { minHeight:"100vh", background:"#f5f7fb", padding:24 },
  wrapper: { maxWidth:1200, margin:"0 auto" },
  backBtn: { border:"none", borderRadius:10, background:"#1f3b64", color:"#fff",
    padding:"10px 14px", cursor:"pointer", fontWeight:700, fontSize:14, marginBottom:18 },
  card:    { background:"#fff", borderRadius:16, boxShadow:"0 4px 18px rgba(0,0,0,0.06)",
    padding:18, marginBottom:18 },
  formGrid: { display:"grid", gridTemplateColumns:"repeat(3, minmax(220px, 1fr))", gap:16, marginBottom:16 },
  label:   { display:"block", marginBottom:6, fontWeight:700, color:"#334155" },
  input:   { width:"100%", padding:"10px 12px", borderRadius:8, border:"1px solid #cbd5e1",
    boxSizing:"border-box", fontSize:14 },
  textarea: { width:"100%", minHeight:90, padding:"10px 12px", borderRadius:8,
    border:"1px solid #cbd5e1", boxSizing:"border-box", resize:"vertical", fontSize:14 },
  saveBtn: { border:"none", borderRadius:8, background:"#1677c8", color:"#fff",
    padding:"12px 16px", cursor:"pointer", fontWeight:700, fontSize:14 },
  saveBtnDisabled: { background:"#94a3b8", cursor:"not-allowed" },
  trimGrid: { display:"grid", gridTemplateColumns:"repeat(2, minmax(280px, 1fr))", gap:18 },
  badge: (bg, color) => ({ background:bg, color, padding:"4px 10px",
    borderRadius:"999px", fontSize:12, fontWeight:700 }),
  toggleBtn: { border:"1px solid #cbd5e1", borderRadius:8, background:"#f8fafc",
    color:"#1f3b64", padding:"8px 12px", cursor:"pointer", fontWeight:700 },
  actCard: { border:"1px solid #e2e8f0", borderRadius:10, padding:12,
    background:"#f8fafc", fontSize:"0.88rem" },
  btnCorregir: { border:"none", borderRadius:8, background:"#f59e0b", color:"#fff",
    padding:"8px 14px", cursor:"pointer", fontWeight:700, fontSize:13 },
  btnCancelar: { border:"1px solid #cbd5e1", borderRadius:8, background:"#fff",
    color:"#475569", padding:"8px 14px", cursor:"pointer", fontSize:13 },
};